import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { ArrowDown } from "lucide-react";

const HERO_IMG = "/hero.jpg";
const SLIDE_SECONDS = 5;

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { t, lang } = useLang();
  const [slides, setSlides] = useState([HERO_IMG]);
  const [isTouch, setIsTouch] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 240]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.35]);

  useEffect(() => {
    setIsTouch(window.matchMedia("(pointer: coarse)").matches);
  }, []);

  useEffect(() => {
    const abs = (u) => (u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
    Promise.all([api.get("/site-images"), api.get("/gallery")])
      .then(([siteRes, galRes]) => {
        const list = [];
        if (siteRes.data?.hero) list.push(abs(siteRes.data.hero));
        (galRes.data || []).filter((p) => p.url !== "/hero.jpg").slice(0, 4).forEach((p) => list.push(abs(p.url)));
        if (list.length > 0) setSlides(list);
      })
      .catch(() => {});
  }, []);

  const n = slides.length;
  const cycle = n * SLIDE_SECONDS;
  const slot = 100 / n;
  const fade = Math.min(4, slot / 4);
  const keyframes = `@keyframes heroFade {
    0% { opacity: 0; transform: scale(1.06); }
    ${fade}% { opacity: 1; }
    ${slot - fade}% { opacity: 1; }
    ${slot}% { opacity: 0; transform: scale(1.16); }
    100% { opacity: 0; transform: scale(1.06); }
  }`;

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[calc(100svh-4rem)] flex items-end overflow-hidden">
      <motion.div style={isTouch ? undefined : { y, opacity }} className="absolute inset-0">
        {n > 1 && <style>{keyframes}</style>}
        {slides.map((src, i) => (
          <img
            key={`${src}-${i}`}
            data-testid={i === 0 ? "hero-image" : undefined}
            src={src}
            alt={i === 0 ? "Funghetto Roma" : ""}
            aria-hidden={i > 0}
            className="absolute inset-0 w-full h-full object-cover"
            style={n > 1 ? { animation: `heroFade ${cycle}s linear ${i * SLIDE_SECONDS}s infinite both` } : undefined}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F6] via-[#FAF9F6]/70 to-[#0A0A0A]/30" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pb-24 pt-16">
        <motion.p
          data-testid="hero-overline"
          className="text-sm uppercase tracking-[0.35em] font-semibold text-[#0A0A0A] mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
        >
          {t.hero.overline}
        </motion.p>
        <h1 data-testid="hero-title" className="font-serif text-[#0A0A0A] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight">
          <MaskedLine delay={0.2}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.35}><span className="italic text-[#6B1E33]">{t.hero.line2}</span></MaskedLine>
        </h1>
        <motion.p
          data-testid="hero-tagline"
          className="mt-5 font-serif italic text-xl sm:text-2xl text-[#6B1E33]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          {t.hero.tagline}
        </motion.p>
        <motion.p
          data-testid="hero-subtitle"
          className="mt-8 max-w-md text-base text-[#333] leading-relaxed"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {t.hero.sub}
        </motion.p>
      </div>

      <motion.a
        href="#gallery"
        data-testid="hero-discover-link"
        className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#555]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <span className="text-[10px] uppercase tracking-[0.35em]">{lang === "en" ? "Discover" : "Scopri"}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.a>
    </section>
  );
}
